import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { EQueryKeys } from 'constants/query-keys';
import { contractApi } from 'services/apis';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import TransactionDetailsSection from './transaction-details';
import { Form, message } from 'antd';
import MidtermAdjustmentsSection from './midterm-adjustments';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FileUploader from '../file-uploader';
import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  ContractAdjustmentRequest,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
} from 'libs/pinnacle-sdk';
import { useFileSession } from 'hooks/useFileSession';
import FormActionButtons from 'components/ui/form-action-buttons';
import { handleError, validateForm } from 'utils/form';
import { ContractAdjustmentValues, getValidationSchema } from './form';
import { isNil } from 'lodash';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import { EKeyboardKey } from 'constants/enums';

const NewContractAdjustment: React.FC = () => {
  const { t } = useTranslation();
  const { contractId } = useParams();
  const [form] = Form.useForm();
  const { fileSessionId, fileList, setFileList } = useFileSession();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isMidtermAdjustmentsValid, setIsMidtermAdjustmentsValid] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: contract } = useQuery({
    queryKey: [EQueryKeys.CONTRACT_BY_ID, contractId],
    queryFn: () =>
      contractApi.getContractById({
        contractId: contractId || '',
        includeAdjustmentCount: true,
        applyAdjustments: true,
      }),
    enabled: !!contractId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    async function checkInitialValues() {
      const values = form.getFieldsValue();
      const isValid = await validateForm(
        values,
        form,
        Object.keys(values),
        getValidationSchema(t, contract)
      );
      setIsFormValid(isValid);
    }
    checkInitialValues();
  }, [form, t, contract]);

  const { mutate: createContractAdjustmentMutation, isLoading } = useMutation({
    mutationFn: (payload: ContractAdjustmentRequest) =>
      contractApi.contractAdjustment(payload),
    onSuccess: () => {
      message.success(t('contractAdjustment.recordCreatedSuccessfully'));
      queryClient.invalidateQueries([EQueryKeys.CONTRACT_BY_ID, contractId]);
      navigate(-1);
    },
    onError: async (error: { response: Response }) => {
      handleError({
        error,
        defaultMessage: t('contractAdjustment.recordCreatedFailed'),
      });
    },
  });

  const handleOnFinish = useCallback(
    async (values: ContractAdjustmentValues) => {
      setShowConfirmation(true);
    },
    []
  );

  const handleConfirmSubmit = useCallback(async () => {
    const values = form.getFieldsValue();
    if (contractId) {
      const payload: ContractAdjustmentRequest = {
        contractId,
        contractAdjustmentPayloadDto: {
          transactionDate: values.transactionDate,
          adjustmentType: values.adjustmentType,
          inceptionDate: values.inceptionDate,
          expiryDate: values.expiryDate,
          ...(values.adjustmentType !==
          ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate
            ? {
                tiv: String(values.totalInsurableValue),
                premiumAdjustmentAmount: String(
                  values.layerPremiumAdjustmentAmount
                ),
                premiumPaymentWarranty: String(values.premiumPaymentWarranty),
                underwritingComments: values.underwritingComments,
              }
            : {}),
          ...(values.adjustmentType ===
          ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement
            ? {
                brokerReference: values.brokerReference,
              }
            : {}),
          cedingCommissionRate: !isNil(values.cedingCommissionRate)
            ? String(values.cedingCommissionRate)
            : undefined,
          gstRateOnCedingCommission: !isNil(values.gstRateOnCedingCommission)
            ? String(values.gstRateOnCedingCommission)
            : undefined,
          sourceBrokerageRate: !isNil(values.sourceBrokerageRate)
            ? String(values.sourceBrokerageRate)
            : undefined,
          gstRateOnPremium: !isNil(values.gstRateOnPremium)
            ? String(values.gstRateOnPremium)
            : undefined,
          binderShareSigned: !isNil(values.binderShareSigned)
            ? String(values.binderShareSigned)
            : undefined,
          ereRate: !isNil(values.ereRate) ? String(values.ereRate) : undefined,
          gstOnEre: !isNil(values.gstOnEre)
            ? String(values.gstOnEre)
            : undefined,
          otherDeductions: !isNil(values.otherDeductions)
            ? String(values.otherDeductions)
            : undefined,
          withholdingTaxRate: !isNil(values.withholdingTaxRate)
            ? String(values.withholdingTaxRate)
            : undefined,
          aitRate: !isNil(values.aitRate) ? String(values.aitRate) : undefined,
          taxAndFeeAmount: !isNil(values.taxAndFeeAmount)
            ? String(values.taxAndFeeAmount)
            : undefined,
          gstRateOnSourceBrokerage: !isNil(values.gstRateOnSourceBrokerage)
            ? String(values.gstRateOnSourceBrokerage)
            : undefined,
          fileIds: fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[],
        },
      };
      setShowConfirmation(false);
      createContractAdjustmentMutation(payload);
    }
  }, [contractId, createContractAdjustmentMutation, fileList, form]);

  return (
    <div className="my-4 rounded-2xl bg-[#E3F2FB] p-3">
      <div className="rounded-lg bg-[#E3F2FB]">
        <div className="rounded-lg px-2 bg-white py-4 space-y-4">
          <h1 className="text-2xl font-semibold px-2">
            {t('contractAdjustment.contractAdjustment')}
          </h1>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={async (_, values) => {
              const isValid = await validateForm(
                values,
                form,
                Object.keys(values),
                getValidationSchema(t, contract)
              );
              setIsFormValid(isValid);
            }}
            onFinish={handleOnFinish}
            onKeyDown={(e) => {
              if (e.key.toLowerCase() === EKeyboardKey.ENTER) {
                e.preventDefault();
              }
            }}
            preserve={true}
          >
            {contract && (
              <TransactionDetailsSection form={form} contract={contract} />
            )}
            {contract && (
              <MidtermAdjustmentsSection
                form={form}
                contract={contract}
                setIsFormValid={setIsMidtermAdjustmentsValid}
              />
            )}

            <FormSection
              title={t('contractAdjustment.uploadFiles')}
              collapsible
            >
              <FormLayoutGroup mdColumns={1}>
                <Form.Item className="mt-6">
                  {fileSessionId && (
                    <FileUploader
                      sessionId={fileSessionId}
                      entityType={
                        CreateUploadedFilePayloadDtoEntityTypeEnum.ContractAdjustment
                      }
                      fileList={fileList}
                      setFileList={setFileList}
                    />
                  )}
                </Form.Item>
              </FormLayoutGroup>
            </FormSection>

            <FormActionButtons
              confirmText={t('general.save')}
              cancelText={t('general.cancel')}
              isConfirmDisabled={
                !isFormValid || isLoading || !isMidtermAdjustmentsValid
              }
              includeCancel={true}
              includeConfirm={true}
              confirmOutlined={false}
              onCancel={() => {
                navigate(-1);
              }}
            />
          </Form>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSubmit}
        title={t('contractAdjustment.confirmSubmissionTitle')}
        content={t('contractAdjustment.confirmSubmissionContent')}
        okText={t('general.yesProceed')}
        cancelText={t('general.cancel')}
        type={ConfirmationModalType.PRIMARY}
      />
    </div>
  );
};

export default NewContractAdjustment;
